import type { ReactNode } from "react"
import Link from "@/components/link"
import { ArrowRight, ArrowUpRight, Code2, MessageSquare } from "lucide-react"

import { StatusLabel } from "@anysola/ui/status-label"
import {
  RowGroup,
  RowIcon,
  rowBase,
  rowInteractive,
} from "@anysola/ui/row-group"
import { UnavailableAlert } from "@/components/feedback/unavailable-alert"
import { BlockHead } from "@/components/layout/block-head"
import { ACCOUNT_URL } from "@/lib/app-links"
import { signInHref } from "@/lib/nav-links"
import { cardLinkClass, rowLinkClass } from "@/lib/ui-classes"
import { formatShortDate } from "@anysola/contracts/shared/relative-time"
import type { PublicChangelogEntry } from "@anysola/contracts/web/changelog-core"
import type { NewsCard } from "@anysola/contracts/web/news-core"
import {
  betaAcceptsApplications,
  betaHref,
  type PublicBeta,
} from "@anysola/contracts/web/public-betas-core"
import { cn } from "@anysola/ui/utils"

// ---------------------------------------------------------------------------
// Get started: three steps, each with the piece of interface it happens in.
// ---------------------------------------------------------------------------

function Step({
  n,
  title,
  href,
  children,
}: {
  n: number
  title: string
  href: string
  children: ReactNode
}) {
  return (
    <li className="flex min-w-0 flex-col rounded-surface bg-card p-2">
      <Link
        href={href}
        className="flex items-baseline gap-3 rounded-xl px-3 pt-2 pb-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="text-muted-foreground tabular-nums">{n}</span>
        <span className="font-medium text-foreground">{title}</span>
        <ArrowRight
          aria-hidden="true"
          className="ml-auto size-3.5 self-center text-muted-foreground"
        />
      </Link>
      <div
        aria-hidden="true"
        className="flex flex-1 flex-col justify-center gap-2 rounded-xl bg-background p-4"
      >
        {children}
      </div>
    </li>
  )
}

function GetStarted() {
  return (
    <section aria-labelledby="start-heading">
      <BlockHead id="start-heading" title="Get started" />
      <ol className="grid gap-3 md:grid-cols-3">
        <Step n={1} title="Create an account" href={signInHref}>
          <span className="rounded-xl bg-card px-3.5 py-2.5 text-[13px] text-muted-foreground">
            alex@example.com
          </span>
          <span className="rounded-full bg-accent py-2 text-center text-[13px] font-medium text-accent-foreground">
            Continue
          </span>
        </Step>
        <Step n={2} title="Pick a plan" href="/pricing">
          <span className="flex gap-1 rounded-full bg-card p-1 text-[13px]">
            {["Free", "Pro", "Team"].map((plan) => (
              <span
                key={plan}
                className={cn(
                  "flex-1 rounded-full py-1.5 text-center",
                  plan === "Pro"
                    ? "bg-default font-medium text-foreground"
                    : "text-muted-foreground"
                )}
              >
                {plan}
              </span>
            ))}
          </span>
          <span className="flex items-center justify-between px-1 pt-1 text-xs text-muted-foreground">
            <span>Credits for every app</span>
            <span className="text-foreground">Monthly</span>
          </span>
        </Step>
        <Step n={3} title="Open any app" href={`${ACCOUNT_URL}/apps`}>
          {[
            { icon: <MessageSquare />, name: "Chat", hint: "chat.anysola.com" },
            { icon: <Code2 />, name: "Cloud", hint: "any_live_••••3f9a" },
          ].map((app) => (
            <span
              key={app.name}
              className="flex items-center gap-3 rounded-xl bg-card px-2.5 py-2"
            >
              <RowIcon>{app.icon}</RowIcon>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] text-foreground">
                  {app.name}
                </span>
                <span className="block truncate font-mono text-[11px] text-muted-foreground">
                  {app.hint}
                </span>
              </span>
              <ArrowUpRight className="size-3.5 text-muted-foreground" />
            </span>
          ))}
        </Step>
      </ol>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Betas: Account's published programs, one row each.
// ---------------------------------------------------------------------------

function Betas({ programs }: { programs: PublicBeta[] }) {
  if (!programs.length) return null
  return (
    <section aria-labelledby="betas-heading">
      <BlockHead
        id="betas-heading"
        title="Betas"
        links={[{ label: "All betas", href: `${ACCOUNT_URL}/betas` }]}
      />
      <RowGroup>
        {programs.slice(0, 4).map((program) => {
          const open = betaAcceptsApplications(program)
          return (
            <li
              key={program.slug}
              className={cn(rowBase, rowInteractive, "relative")}
            >
              <span className="min-w-0 flex-1">
                <Link
                  href={betaHref(program, ACCOUNT_URL)}
                  className={cn(rowLinkClass, "block truncate")}
                >
                  {program.name}
                </Link>
                <span className="mt-0.5 block truncate text-sm text-muted-foreground">
                  {program.description}
                </span>
              </span>
              <StatusLabel tone={open ? "success" : "muted"}>
                {open ? "Applications open" : "Closed"}
              </StatusLabel>
            </li>
          )
        })}
      </RowGroup>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Latest: the newest news posts and changelog entries together, four boxes.
// ---------------------------------------------------------------------------

type LatestItem = {
  key: string
  kind: "News" | "Changelog"
  label: string
  date: string
  title: string
  href: string
}

/**
 * `null` is a list that couldn't be read: the block says so rather than
 * dropping out, so an outage never looks like nothing was published.
 */
function Latest({
  news,
  changelog,
}: {
  news: NewsCard[] | null
  changelog: PublicChangelogEntry[] | null
}) {
  const unreadable =
    news === null && changelog === null
      ? "News and the changelog are unavailable right now."
      : news === null
        ? "News is unavailable right now."
        : changelog === null
          ? "The changelog is unavailable right now."
          : null
  const items: LatestItem[] = [
    ...(news ?? []).map((post) => ({
      key: `n-${post.id}`,
      kind: "News" as const,
      label: post.category,
      date: post.date,
      title: post.title,
      href: `/news/${post.slug}`,
    })),
    ...(changelog ?? []).map((entry) => ({
      key: `c-${entry.id}`,
      kind: "Changelog" as const,
      label: entry.tag,
      date: entry.date,
      title: entry.title,
      href: `/changelog#${entry.slug}`,
    })),
  ]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 4)
  if (!items.length && !unreadable) return null

  return (
    <section aria-labelledby="latest-heading">
      <BlockHead
        id="latest-heading"
        title="Latest"
        links={[
          ...(news?.length ? [{ label: "News", href: "/news" }] : []),
          ...(changelog?.length
            ? [{ label: "Changelog", href: "/changelog" }]
            : []),
        ]}
      />
      {unreadable && (
        <UnavailableAlert
          title={unreadable}
          className={cn(items.length > 0 && "mb-3")}
        />
      )}
      {items.length > 0 && (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <li key={item.key}>
              <Link
                href={item.href}
                className={cn(cardLinkClass, "min-h-28 sm:min-h-36")}
              >
                <span className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span className="truncate">
                    {item.kind} · {item.label}
                  </span>
                  <time dateTime={item.date} className="shrink-0">
                    {formatShortDate(item.date)}
                  </time>
                </span>
                <span className="mt-auto pt-6 text-[0.9375rem] leading-snug font-medium tracking-tight text-foreground">
                  {item.title}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export { Betas, GetStarted, Latest }
